import type {
  AccountsUsageInfo,
  BillingCancelOrdwaySubscriptionRequest,
  BillingCancelRequest,
  BillingGetInfoResponse,
  BillingInfo,
  BillingOrdwayCouponModel,
  BillingOrdwayCustomerModel,
  BillingOrdwayProfileResponse,
  BillingOrdwaySubscriptionModel,
  BillingOrdwaySubscriptionRequest,
  BillingPauseRequest,
  Coupon,
  GithubComStripeStripeGoV71SetupIntent,
  TaxRate,
  Tier,
} from '@customerio/hydra-ui-api';
import { announceRefreshAuth } from '@customerio/hydra-nav-state';
import plur from 'plur';
import { atom, computed } from 'nanostores';
import {
  differenceInDays,
  endOfDay,
  format,
  formatDistanceToNow,
  fromUnixTime,
  getUnixTime,
  parseISO,
  startOfDay,
} from 'date-fns';
import { hydraUiApi } from '@/services/api';
import { AccountState, VerificationState, type AugmentedAccount } from '@/services/auth/types';
import { accountStore, userStore } from '@/services/auth/store';
import { masqReadOnlyStore } from '@/utils/masq';
import type { AccountPermission } from '@customerio/hydra-permissions';
import { checkPermission } from '@customerio/hydra-permissions/validation';
import { BillingSystem, type BillingEstimates, type PaymentCard } from './types';
import { stripeCustomerToPaymentCard } from './stripe/card';
import { ordwayPaymentToPaymentCard } from './ordway/card';
import { stripeFormatter } from './stripe/errors';
import { MASQ_READ_ONLY_MESSAGE, NO_BILLING_PERMISSION_MESSAGE, VERIFICATION_REJECTED_MESSAGE } from './messages';
import { enqueueTask } from '@/utils/enqueue-task';
import { pollDelay, pollTask } from '@/utils/poll-task';
import { getCrossAppCache, setCrossAppCache } from '@/utils/cross-app-cache';
import { isEmptyString } from '@/utils/string';
import { snackbarApi } from '@/services/snackbar';
import { sentryCaptureException } from '@/utils/sentry';
import type { ProductApp } from './plans';
import { getUpgradePlanTypes, Plans, PlanType, StartupType } from './plans';
import { baseOrdwayProductIds, ordwayProductIds, ProductName } from './ordway/products';
import { OrdwayBillingPeriod } from './ordway/plans';
import {
  ordwayPlanToBillingPlan,
  ordwaySubscriptionToBillingPlan,
  stripePlanToBillingPlan,
  stripeSubscriptionToBillingData,
  type OrdwayPlanBillingPlan,
  type OrdwaySubscriptionBillingPlan,
  type StripeBillingPlan,
  type StripeSubscriptionBillingData,
  type StripePlanContext,
} from './transforms';
import { type OrdwayCustomerCustomFields } from './ordway/types';

/** Feature-flag key controlling the "new pipelines objects pricing" plan highlights. */
const NEW_PIPELINES_OBJECTS_PRICING_FLAG = 'new_pipelines_objects_pricing';

/** A single product line on an Ordway subscription (`subscription.plans[]`). */
export type OrdwaySubscriptionProduct = NonNullable<BillingOrdwaySubscriptionModel['plans']>[number];

/**
 * The effective subscription stored on {@link billing.subscription}. For Ordway accounts
 * this is the Ordway subscription transformed into the shared `BillingPlan` shape (raw
 * wire fields preserved side-by-side). For legacy Stripe accounts it is the raw Stripe
 * subscription with its `plan` resolved to a `StripeBillingPlan` and a `hasCard` flag
 * added (raw wire fields preserved side-by-side).
 */
export type BillingSubscription = StripeSubscriptionBillingData | OrdwaySubscriptionBillingPlan;

/** A coupon is either the Stripe model or the Ordway model. */
export type BillingCoupon = Coupon | BillingOrdwayCouponModel;

/** Options for {@link billing.initialize}. Mirrors the Ember billing service's `initialize` task. */
export interface InitializeBillingOptions {
  /** Re-fetch even when already initialized (subject to a short throttle). */
  refresh?: boolean;
  /** Swallow request errors and still mark the service initialized. */
  ignoreErrors?: boolean;
  /** Plan slug from a URL query param; selects {@link billing.paramPlan} from the returned plans. Bypasses the refresh throttle. */
  paramPlanSlug?: string;
  /** Whether to include coupon details in the Ordway profile response. */
  includeCoupons?: boolean;
}

/**
 * Refresh throttle (ms). Mirrors the Ember billing service: when already
 * initialized and refreshed without a coupon/plan param, suppress refreshes
 * that land within this window (the user is likely just being redirected).
 */
const RECENTLY_INITIALIZED_WINDOW_MS = 500;

/** Plan slug identifying the Stripe Essentials plan. Mirrors the Ember `ESSENTIALS_PLAN_SLUG`. */
const ESSENTIALS_PLAN_SLUG = '202301_pricing_essentials';

/** Status-poll limits, matching the Ember `pollForSubscriptionTask` / `pollForCreditsPurchaseTask`. */
const POLL_MAX_ATTEMPTS = 15;
const POLL_INTERVAL_MS = 1000;

/**
 * How long a billing response stays usable from the cross-app cache (ms). When the
 * shell repeatedly boots the embedded apps, each boot would otherwise re-request the
 * billing endpoints and trip 429s from the billing vendor. Caching the response lets
 * non-refresh `initialize` calls — in this app or in the embedded Ember app, which
 * shares `window.__hydra_cross_app_cache__` — reuse a recent response instead of
 * hitting the network. Mirrors the Ember billing service's `BILLING_CACHE_TTL_MS`.
 */
const BILLING_CACHE_TTL_MS = 30_000;

/**
 * Loads the Ordway billing profile, reusing a recent cross-app-cached response when
 * available. Non-refresh calls read the cache first; a cache miss (or `refresh`) goes
 * to the network and writes the response back. The cache key matches the Ember billing
 * service exactly so the two apps share cached responses.
 */
async function loadOrdwayProfile(
  accountId: number,
  includeCoupons: boolean,
  refresh: boolean,
): Promise<BillingOrdwayProfileResponse> {
  const cacheKey = `/accounts/${accountId}/billing/profile?coupons=${includeCoupons}`;
  const cached = refresh ? null : getCrossAppCache(cacheKey);

  if (cached != null) {
    return cached as BillingOrdwayProfileResponse;
  }

  const res = await hydraUiApi.billing.getProfile({
    params: { path: { account_id: String(accountId) }, query: { coupons: String(includeCoupons) } },
  });
  setCrossAppCache({ key: cacheKey, value: res, ttl: BILLING_CACHE_TTL_MS });
  return res;
}

/**
 * Loads the legacy Stripe billing info, reusing a recent cross-app-cached response when
 * available (see {@link loadOrdwayProfile}). The `code=` segment in the key is
 * intentionally empty — coupon-code / `extra_plan` request params are not yet wired up
 * here — but still matches the Ember key for the common (no coupon code) boot path.
 */
async function loadStripeInfo(
  accountId: number,
  paramPlanSlug: string,
  refresh: boolean,
): Promise<BillingGetInfoResponse> {
  const cacheKey = `/accounts/${accountId}/billing/info?code=&extra_plan=${paramPlanSlug}`;
  const cached = refresh ? null : getCrossAppCache(cacheKey);

  if (cached != null) {
    return cached as BillingGetInfoResponse;
  }

  const res = await hydraUiApi.billing.getInfo({
    params: { path: { account_id: String(accountId) } },
  });
  setCrossAppCache({ key: cacheKey, value: res, ttl: BILLING_CACHE_TTL_MS });
  return res;
}

/**
 * Loads the account usage info (allotments, per-environment usage), reusing a recent cross-app-cached
 * response when available (see {@link loadStripeInfo}). Loaded lazily via `billing.loadUsage` — the
 * billing service is the canonical gateway for usage, mirroring the Ember routes that
 * `store.queryRecord('account-usage')` on demand rather than on boot.
 */
async function loadAccountUsage(accountId: number, refresh: boolean): Promise<AccountsUsageInfo | undefined> {
  const cacheKey = `/accounts/${accountId}/usage`;
  const cached = refresh ? null : getCrossAppCache(cacheKey);

  if (cached != null) {
    return cached as AccountsUsageInfo;
  }

  const res = await hydraUiApi.accounts.getUsage({ params: { path: { account_id: String(accountId) } } });
  const usage = res.account_usage ?? undefined;
  if (usage != null) {
    setCrossAppCache({ key: cacheKey, value: usage, ttl: BILLING_CACHE_TTL_MS });
  }
  return usage;
}

/**
 * Sorts Ordway subscriptions ascending by service start date, oldest first.
 * Subscriptions without a start date sort last (matching lodash `sortBy`).
 */
function sortOrdwaySubscriptionsByDate(
  subscriptions: readonly BillingOrdwaySubscriptionModel[],
): BillingOrdwaySubscriptionModel[] {
  return [...subscriptions].sort((a, b) => {
    const aTime = isEmptyString(a.service_start_date) ? Infinity : Date.parse(a.service_start_date ?? '');
    const bTime = isEmptyString(b.service_start_date) ? Infinity : Date.parse(b.service_start_date ?? '');
    return aTime - bTime;
  });
}

/**
 * The backend does not filter future-subscription products the way it filters
 * the effective subscription's, so collapse duplicate `product_id`s here. When
 * a product has no charge start date it's aligned with the service start date
 * and wins; otherwise the earliest charge start date wins. Ports Ember
 * `#setOrdwayBillingProperties`.
 */
function dedupeFutureSubscriptionProducts(products: readonly OrdwaySubscriptionProduct[]): OrdwaySubscriptionProduct[] {
  const productById: Record<string, OrdwaySubscriptionProduct> = {};

  for (const product of products) {
    const existing = productById[product.product_id];

    if (existing == null) {
      productById[product.product_id] = product;
      continue;
    }

    if (isEmptyString(product.charge_start_date)) {
      // No charge start date means the product is aligned with the service
      // start date — prefer it.
      productById[product.product_id] = product;
    } else if (!isEmptyString(existing.charge_start_date)) {
      // Both have a charge start date: keep the earliest.
      if (Date.parse(product.charge_start_date ?? '') < Date.parse(existing.charge_start_date ?? '')) {
        productById[product.product_id] = product;
      }
    }
  }

  return Object.values(productById);
}

/**
 * Singleton billing service for the Hydra React app.
 *
 * Modelled as a plain object — a curated public API rather than a class or a
 * module that re-exports everything — following the "limiting public APIs"
 * pattern (https://zenn.dev/dk_/articles/bc779b8fba68b0?locale=en#limiting-public-apis).
 *
 * State is held in nanostore atoms so the UI can subscribe and re-render.
 * Account-derived flags are computeds over the shared `accountStore`, so they
 * track account changes without any explicit wiring. Exposed on
 * `window.__hydra__.billing` for cross-app (Ember) consumption.
 */
/**
 * Builds the {@link StripePlanContext} used to transform Stripe plans. In Ember these
 * values came from the billing/features services via getters; here they're captured once
 * from the billing info and account at load time, keeping the transform pure. Mirrors the
 * Ember `PlanModel` getters: `canCancel` ← `billingInfo.cancellable`, the feature-group
 * selection ← `billing.isTrial` (which is `premium_trial || basic_trial`, or the account's
 * `suspend_trial_at` when there is no billing info) and `billingInfo.startupProgram`, and
 * the highlights ← the `new_pipelines_objects_pricing` feature flag.
 */
// fallow-ignore-next-line complexity — ported from Ember billing, complexity is inherent to the domain mapping
function stripePlanContext(billingInfo: BillingInfo | undefined): StripePlanContext {
  const account = accountStore.get();
  const isAccountTrial =
    billingInfo == null ? account?.suspend_trial_at != null : billingInfo.basic_trial || billingInfo.premium_trial;

  return {
    cancellable: billingInfo?.cancellable ?? false,
    isAccountTrial,
    startupProgram: isEmptyString(billingInfo?.startup_program)
      ? undefined
      : (billingInfo?.startup_program as StartupType),
    newPipelinesObjectsPricing: account?.feature_flags?.[NEW_PIPELINES_OBJECTS_PRICING_FLAG] ?? false,
  };
}

/** The Stripe subscription's saved card in the neutral {@link PaymentCard} shape, or null when absent. */
function stripeSubscriptionCard(subscription: BillingGetInfoResponse['subscription']): PaymentCard | null {
  const customer = subscription?.stripe_customer;
  // Null when there's no card on file — mirror the `hasCard` derivation (empty `card_type`), so
  // `cardDetails` stays null whenever `hasCard` is false rather than exposing an empty card.
  if (customer == null || isEmptyString(customer.card_type)) {
    return null;
  }
  return stripeCustomerToPaymentCard(customer);
}

// Ordway subscription discriminator. The subscription atom holds a Stripe *or* Ordway subscription
// tagged with a `billingSystem` discriminant, so the union narrows on that field.
function isOrdwaySubscription(
  subscription: BillingSubscription | undefined,
): subscription is OrdwaySubscriptionBillingPlan {
  return subscription?.billingSystem === BillingSystem.Ordway;
}

function isStripeSubscription(
  subscription: BillingSubscription | undefined,
): subscription is StripeSubscriptionBillingData {
  return subscription?.billingSystem === BillingSystem.Stripe;
}

// Ember computed Ordway trial dates with `moment.utc(date).startOf('day').unix()`. Reproduce the
// UTC-start-of-day-to-unix-seconds conversion without moment by zeroing the time in UTC.
function utcStartOfDayUnix(date: string): number {
  const parsed = new Date(date);
  return Math.floor(Date.UTC(parsed.getUTCFullYear(), parsed.getUTCMonth(), parsed.getUTCDate()) / 1000);
}

// Local-timezone start-of-day-to-unix-seconds, matching the Ember `billingPeriodStart`'s
// `moment(date).startOf('day').unix()` (local, unlike the UTC helper above). `parseISO` treats a
// date-only string as local midnight (as moment does), so this mirrors the Ember behavior.
function localStartOfDayUnix(date: string): number {
  return getUnixTime(startOfDay(parseISO(date)));
}

// Local-timezone end-of-day-to-unix-seconds, matching the Ember `couponEndsAt`'s
// `moment(date).endOf('day').unix()` (local, like `localStartOfDayUnix`). Used only for the
// Ordway coupon expiration date.
function localEndOfDayUnix(date: string): number {
  return getUnixTime(endOfDay(parseISO(date)));
}

// Whether the effective Ordway subscription carries the product with the given name's id.
function subscriptionIncludesProduct(subscription: BillingSubscription | undefined, name: ProductName): boolean {
  if (!isOrdwaySubscription(subscription)) {
    return false;
  }

  const productId = ordwayProductIds()[name];
  return (subscription.plans ?? []).some((product) => product.product_id === productId);
}

/**
 * Synchronous account-permission check, colocated here because billing is (for now) the only
 * consumer that needs to check permissions outside the React tree — most React code should use
 * the `usePermissions` hook instead. `checkPermission` resolves account permissions from
 * `user.account_permissions` before it consults workspace roles, so passing `roles`/`workspaceId`
 * as null is sufficient for account-level permissions like `account/billing/edit`.
 */
function hasAccountPermission(permission: AccountPermission): boolean {
  const user = userStore.get();
  const account = accountStore.get();

  if (user == null || account == null) {
    return false;
  }

  return checkPermission(user, account, null, permission, null);
}

// The Ember masq service's `readOnly` is only ever true during a masquerade session, so gate the
// stored toggle on the account's masq flag to reproduce that. Note the permission checks already
// deny edits during a read-only masquerade; this is only used to surface the read-only *message*.
function isMasqReadOnly(account: AugmentedAccount | null): boolean {
  return account?.is_masq === true && masqReadOnlyStore.get();
}

// The billing atoms and their derived computeds are defined at module scope so the computeds can
// list the atoms as dependencies (an object literal can't reference its own siblings). They're
// exposed on the `billing` object below via shorthand, so `billing.subscription`,
// `billing.isTrial`, etc. are the same atoms/computeds defined here.

// Billing data atoms.
const subscription = atom<BillingSubscription | undefined>(undefined);
const coupon = atom<BillingCoupon | undefined>(undefined);
const taxRate = atom<TaxRate | undefined>(undefined);
/** Plan matching `paramPlanSlug`, resolved from `plans`. */
const paramPlan = atom<StripeBillingPlan | null>(null);
/** Legacy stripe billing info. Undefined for Ordway accounts. */
const billingInfo = atom<BillingInfo | undefined>(undefined);
/** Stripe plans transformed into the shared `BillingPlan` shape. Undefined for Ordway accounts. */
const plans = atom<StripeBillingPlan[] | undefined>(undefined);
const tiers = atom<Tier[] | undefined>(undefined);
/** Ordway billing data. Undefined for legacy stripe and metronome accounts. */
const customer = atom<BillingOrdwayCustomerModel | undefined>(undefined);
/** The upcoming Ordway subscription, transformed into the shared `BillingPlan` shape. */
const futureSubscription = atom<OrdwaySubscriptionBillingPlan | undefined>(undefined);
const products = atom<OrdwaySubscriptionProduct[] | undefined>(undefined);
/** Account usage (allotments + per-environment usage). Loaded lazily via `loadUsage`, undefined until then. */
const usage = atom<AccountsUsageInfo | undefined>(undefined);
/**
 * Available Ordway plans catalog, loaded on demand via `fetchAvailablePlans`. Undefined until
 * loaded; unused for Stripe accounts (whose plans arrive with `billing/info`).
 */
const availablePlans = atom<OrdwayPlanBillingPlan[] | undefined>(undefined);
/**
 * Ordway billing overage estimate, computed from the `billing/overage_estimate` endpoint. Written via
 * {@link billing.setOrdwayBillingEstimate}; read (for Ordway accounts) by {@link billing.billingEstimates}.
 */
const ordwayBillingEstimate = atom<BillingEstimates | undefined>(undefined);
/** Whether the account is eligible for the retention discount. Written via {@link billing.setRetentionDiscountEligible}. */
const retentionDiscountEligible = atom(false);
/**
 * The account's saved card, in the vendor-neutral {@link PaymentCard} shape. Set by
 * {@link billing.setStripeBillingProperties} (from the subscription's `stripe_customer`) and by
 * {@link billing.fetchOrdwayCardDetails} (from the fetched Ordway payment method); read via
 * {@link billing.cardDetails}. Null when the account has no card on file.
 */
const card = atom<PaymentCard | null>(null);
const initialized = atom(false);

// Account-derived flags.
const isOrdwayCustomer = computed(
  accountStore,
  (account) => account?.ordway_id != null && account.ordway_id.length > 0,
);
const isOrdwayCustomerWaitingInitialization = computed(
  accountStore,
  (account) => account?.ordway_id?.startsWith('TEMP-') === true,
);
/** Whether the account's verification has been rejected. */
const isVerificationRejected = computed(
  accountStore,
  (account) => account?.verification_state === VerificationState.Rejected,
);

// Derived, reactive billing state.

/** Whether the account is on a premium trial. */
const isPremiumTrial = computed([accountStore, subscription, billingInfo], (account, sub, info) => {
  if (sub?.billingSystem === BillingSystem.Ordway) {
    return account?.planFeatures.premium_trial.enabled === true;
  }

  return info?.premium_trial ?? false;
});

/** Whether the account has added a payment method but its trial hasn't finished yet. */
const isPendingCheckout = computed(
  [accountStore, subscription, customer, futureSubscription, isPremiumTrial],
  (account, sub, cust, future, premiumTrial) => {
    if (!isOrdwaySubscription(sub)) {
      return false;
    }

    // when the customer has added a payment method but their trial isn't finished yet
    const accountState = account?.account_state;
    const isCurrentOrFree = accountState === AccountState.Current || accountState === AccountState.Free;

    return (
      (isCurrentOrFree || cust?.has_credit_card === true) && // has a payment method / active account
      (sub.isTrial || premiumTrial) && // is currently on a free trial
      future != null // has an upcoming subscription
    );
  },
);

/** Whether the account is on a trial (across Ordway and legacy Stripe billing). */
const isTrial = computed(
  [accountStore, subscription, billingInfo, isPendingCheckout],
  // fallow-ignore-next-line complexity — ported from Ember billing, complexity is inherent to the domain mapping
  (account, sub, info, pendingCheckout) => {
    const isOrdwayAccount = account?.ordway_id != null && account.ordway_id.length > 0;

    if (isOrdwayAccount || sub?.billingSystem === BillingSystem.Ordway) {
      const isWaitingInitialization = account?.ordway_id?.startsWith('TEMP-') === true;
      return isWaitingInitialization || (isOrdwaySubscription(sub) && sub.isTrial === true) || pendingCheckout;
    }

    // The billing-info trial flag (premium_trial OR basic_trial).
    const billingInfoTrial = info == null ? undefined : info.premium_trial || info.basic_trial;
    return billingInfoTrial ?? account?.suspend_trial_at != null;
  },
);

/** The pinned plan for the account, or null (Ordway never has pinned plans). */
const pinnedPlan = computed([subscription, billingInfo, plans], (sub, info, planList) => {
  // Ordway will never have pinned plans in the stripe system sense.
  if (sub?.billingSystem === BillingSystem.Ordway) {
    return null;
  }

  const pinnedPlanId = info?.pinned_plan_id;
  if (pinnedPlanId == null) {
    return null;
  }

  return planList?.find((plan) => plan.id === pinnedPlanId) ?? null;
});

/**
 * The Essentials plan for the account: for Ordway, the Essentials entry in the loaded
 * `availablePlans` catalog; for Stripe, the plan in `plans` matching `ESSENTIALS_PLAN_SLUG`.
 * Ports the Ember `billing.essentialsPlan` getter. Undefined until the relevant plans load.
 */
const essentialsPlan = computed(
  [isOrdwayCustomer, availablePlans, plans],
  (ordwayCustomer, ordwayPlans, stripePlans): OrdwayPlanBillingPlan | StripeBillingPlan | undefined => {
    if (ordwayCustomer) {
      return (ordwayPlans ?? []).find((plan) => plan.planType === PlanType.Essentials);
    }

    return (stripePlans ?? []).find((plan) => plan.slug === ESSENTIALS_PLAN_SLUG);
  },
);

/**
 * The start of the current billing period, as unix seconds, or undefined. Ordway reads the
 * subscription's period/service start (local start-of-day); otherwise the account's
 * plan_period_start.
 */
const billingPeriodStart = computed([subscription, accountStore], (sub, account) => {
  if (isOrdwaySubscription(sub)) {
    // The Ordway wire model represents unset dates as empty strings, not null, so fall back to
    // service_start_date when billing_period_start_date is empty (not just null/undefined).
    const startDate = isEmptyString(sub.billing_period_start_date)
      ? sub.service_start_date
      : sub.billing_period_start_date;
    return isEmptyString(startDate) ? undefined : localStartOfDayUnix(startDate);
  }

  return account?.plan_period_start;
});

/**
 * The end of the current billing period, as unix seconds, or undefined. Ordway: the end of the UTC
 * day before `usage_reset_date`, equivalently the UTC start-of-day minus one second. Otherwise the
 * account's plan_period_end.
 */
const billingPeriodEnd = computed([subscription, accountStore], (sub, account) => {
  if (isOrdwaySubscription(sub)) {
    return isEmptyString(sub.usage_reset_date) ? undefined : utcStartOfDayUnix(sub.usage_reset_date) - 1;
  }

  return account?.plan_period_end;
});

/**
 * The profile allotment, taken from the Ordway subscription, the pinned plan, the active Stripe
 * plan, or (plan-less accounts) the account usage allotment. `usage` is loaded lazily via
 * `loadUsage`; when it hasn't been loaded the plan-less fallback is 0.
 */
const includedProfiles = computed([subscription, pinnedPlan, usage], (sub, pinned, accountUsage) => {
  if (isOrdwaySubscription(sub)) {
    return sub.includedProfiles;
  }

  if (pinned != null) {
    return pinned.includedProfiles;
  }

  return (isStripeSubscription(sub) ? sub.plan?.includedProfiles : undefined) ?? accountUsage?.customers_allotted ?? 0;
});

/** The message allotment for the account. */
const includedMessages = computed([subscription, pinnedPlan, usage], (sub, pinned, accountUsage) => {
  if (isOrdwaySubscription(sub)) {
    return sub.includedMessages;
  }

  if (pinned != null) {
    return pinned.includedMessages;
  }

  return (isStripeSubscription(sub) ? sub.plan?.includedMessages : undefined) ?? accountUsage?.messages_allotted ?? 0;
});

/** The object allotment for the account. */
const includedObjects = computed([subscription, pinnedPlan, usage], (sub, pinned, accountUsage) => {
  if (isOrdwaySubscription(sub)) {
    return sub.includedObjects;
  }

  if (pinned != null) {
    return pinned.includedObjects;
  }

  return (isStripeSubscription(sub) ? sub.plan?.includedObjects : undefined) ?? accountUsage?.objects_allotted ?? 0;
});

/** The monthly-active-user allotment for the account (Ordway only). */
const includedMau = computed([subscription], (sub) => {
  return isOrdwaySubscription(sub) ? (sub.includedMau ?? 0) : 0;
});

/**
 * Total customer count across all environments. Exposed on the billing service so consumers read
 * usage totals through the billing gateway.
 */
const totalCustomerCount = computed([usage], (accountUsage) => {
  return (accountUsage?.environment_usages ?? []).reduce((sum, environment) => sum + environment.customers_total, 0);
});

/** The message explaining why an action is disabled for the given billing permission, if any. */
function disabledMessageForPermission(
  permission: AccountPermission,
  permissionMessage: string = NO_BILLING_PERMISSION_MESSAGE,
): string {
  if (isVerificationRejected.get()) {
    return VERIFICATION_REJECTED_MESSAGE;
  }
  if (isMasqReadOnly(accountStore.get())) {
    return MASQ_READ_ONLY_MESSAGE;
  }
  if (!hasAccountPermission(permission)) {
    return permissionMessage;
  }
  return '';
}

/** Whether upgrading is disabled (rejected verification, read-only masquerade, or no permission). */
const isUpgradeDisabled = computed(
  [accountStore, userStore, masqReadOnlyStore, isVerificationRejected],
  (account, _user, _masqReadOnly, verificationRejected) => {
    return verificationRejected || isMasqReadOnly(account) || !hasAccountPermission('account/billing/edit');
  },
);

/** The message explaining why upgrading is disabled, if it is. */
const upgradeDisabledMessage = computed([accountStore, userStore, masqReadOnlyStore, isVerificationRejected], () =>
  disabledMessageForPermission('account/billing/edit'),
);

// --- Plan identity & naming. ---

/** The plan type for the account. */
const planType = computed([subscription], (sub): PlanType | undefined => {
  if (isOrdwaySubscription(sub)) {
    return sub.planType;
  }

  return isStripeSubscription(sub) ? sub.plan?.planType : undefined;
});

/** The plan type of the upcoming subscription (Ordway only). */
const futurePlanType = computed([subscription, futureSubscription], (sub, future): PlanType | undefined => {
  if (isOrdwaySubscription(sub)) {
    return future?.planType;
  }

  return undefined;
});

/** The plan slug for the account (empty string for Ordway). */
const planSlug = computed([subscription], (sub): string | undefined => {
  if (isOrdwaySubscription(sub)) {
    return '';
  }

  return isStripeSubscription(sub) ? sub.plan?.slug : undefined;
});

/** Whether the account is on a basic or essentials plan. */
const isBasic = computed([subscription, billingInfo, planType], (sub, info, type) => {
  if (isOrdwaySubscription(sub)) {
    if (type == null) {
      return false;
    }

    return type === PlanType.Basic || type === PlanType.Essentials;
  }

  return info?.basic ?? false;
});

/** Whether the account is on a premium plan (or premium trial). */
const isPremium = computed([subscription, billingInfo, isPremiumTrial, planType], (sub, info, premiumTrial, type) => {
  if (isOrdwaySubscription(sub)) {
    return premiumTrial || type === PlanType.Premium;
  }

  return info?.premium ?? false;
});

/**
 * Whether the account is on a basic (Essentials) trial. Ports the Ember `billing.isBasicTrial`:
 * an Ordway account derives it from the trial + basic computeds; legacy Stripe reads the
 * billing-info `basic_trial` flag.
 */
const isBasicTrial = computed([subscription, isTrial, isBasic, billingInfo], (sub, trial, basic, info) => {
  if (isOrdwaySubscription(sub)) {
    return trial && basic;
  }

  return info?.basic_trial ?? false;
});

/** The shortened plan name, with a "Trial" suffix while on a premium trial. */
const planNameShortened = computed(
  [subscription, billingInfo, plans, paramPlan, isBasic, isPremium, isTrial, isPremiumTrial],
  // fallow-ignore-next-line complexity — ported from Ember billing, complexity is inherent to the domain mapping
  (sub, info, planList, param, basic, premium, trial, premiumTrial): string => {
    if (info == null && sub == null) {
      return '';
    }

    let plan: string;

    if (isOrdwaySubscription(sub)) {
      plan = sub.name;
    } else if (basic) {
      // main Customer.io Essentials plan as of Jan 2023:
      const subscriptionPlanId = isStripeSubscription(sub) ? sub.plan?.id : undefined;
      plan = planList?.find((candidate) => candidate.id === subscriptionPlanId)?.name ?? 'Essentials';
    } else if (trial && premium) {
      // premium trial: use plan name based on URL param, fallback to generic "Premium"
      plan = param != null ? param.name : 'Premium';
    } else {
      // any active custom plan (both premium, non-premium):
      plan = (isStripeSubscription(sub) ? sub.plan?.name : undefined) ?? '';
    }

    if (premiumTrial) {
      return `${plan} Trial`;
    }

    return plan;
  },
);

/** The plan's display name, falling back to the shortened plan name. */
const planDisplayName = computed([subscription, planNameShortened], (sub, nameShort): string => {
  if (isOrdwaySubscription(sub)) {
    return nameShort;
  }

  return (isStripeSubscription(sub) ? sub.plan?.display_name : undefined) ?? nameShort;
});

/** Whether the account is on the Builder plan. */
const isBuilder = computed([accountStore], (account) => account?.plan_slug === PlanType.Builder);

/** Whether the account is on the Builder plan (an alias of `isBuilder`). */
const isBuilderPlan = computed([isBuilder], (builder) => builder);

/** Whether the account is on the free Builder plan. */
const isFreeBuilder = computed([isBuilder, accountStore], (builder, account) => {
  return builder && account?.account_state === AccountState.Free;
});

/** The plan types the account can upgrade to, keyed by product app. */
const upgradePlanTypes = computed(
  [isPremiumTrial, accountStore, planType],
  (premiumTrial, account, type): Record<ProductApp, PlanType> => {
    return getUpgradePlanTypes(premiumTrial, account?.planFeatures.messaging.expired ?? false, type);
  },
);

// --- Subscription lifecycle & date derivations. ---

/** Typed view of the loose `custom_fields` bag on an Ordway customer, for the cancellation date. */
function ordwayCustomerCustomFields(customer: BillingOrdwayCustomerModel): OrdwayCustomerCustomFields {
  return (customer.custom_fields ?? {}) as OrdwayCustomerCustomFields;
}

/** Whether the account has an active (non-trial) subscription. */
const isSubscriptionActive = computed([subscription, isTrial], (sub, trial) => {
  if (isOrdwaySubscription(sub)) {
    return trial === false;
  }

  return isStripeSubscription(sub) ? sub.active : false;
});

/**
 * The pinned start date (unix seconds). Ordway never has pinned plans in the Stripe sense, so it's
 * undefined there; otherwise the billing info's `pinned_start_date`.
 */
const pinnedStartDate = computed([subscription, billingInfo], (sub, info): number | undefined => {
  if (isOrdwaySubscription(sub)) {
    return undefined;
  }

  return info?.pinned_start_date;
});

/**
 * The formatted subscription start date. Ordway formats the upcoming (or effective) subscription's
 * UTC start-of-day; Stripe formats the pinned start date (unix seconds). Both render the instant in
 * the runtime's local timezone: because the value is UTC start-of-day rendered locally, a
 * UTC-negative timezone shows the previous calendar day (e.g. `2025-06-01` renders "31st May 2025"
 * in the Americas). That local-shift is intentional; do not "fix" it to a UTC render here. If the
 * shift is undesirable, it's a product decision to change deliberately.
 */
const formattedStartDate = computed([subscription, futureSubscription, billingInfo], (sub, future, info) => {
  if (isOrdwaySubscription(sub)) {
    const date = future?.billing_start_date ?? sub.billing_start_date;
    // Guard against a missing/invalid start date: `format` throws on an invalid Date, and a throw
    // inside a nanostores computed corrupts the shared notify queue for every other computed. A
    // real Ordway subscription always carries a billing_start_date, so this only trips on sparse
    // test data; return an empty string rather than throwing.
    if (isEmptyString(date)) {
      return '';
    }
    return format(fromUnixTime(utcStartOfDayUnix(date)), 'do MMMM yyyy');
  }

  // Non-Ordway branch, so the pinned start date is `billingInfo.pinned_start_date` (see `pinnedStartDate`).
  // Read it directly rather than via the `pinnedStartDate` computed to avoid a computed-on-computed
  // diamond over `subscription`.
  return format(fromUnixTime(info?.pinned_start_date ?? 0), 'do MMMM yyyy');
});

/**
 * Whether the plan is billed annually. Ordway reads the base product's billing period; Stripe reads
 * the subscription's `annual` flag.
 */
const isAnnualPlan = computed([subscription], (sub) => {
  if (isOrdwaySubscription(sub)) {
    const baseProduct = sub.plans?.find((product) => baseOrdwayProductIds().includes(product.product_id));

    if (baseProduct == null) {
      return false;
    }

    return baseProduct.billing_period === OrdwayBillingPeriod.Annually;
  }

  return isStripeSubscription(sub) ? sub.annual : false;
});

/** Whether the plan is billed as a lump sum. */
const isLumpSumPlan = computed([subscription, billingInfo], (sub, info) => {
  if (isOrdwaySubscription(sub)) {
    return sub.isLumpSum === true;
  }

  return info?.lump_sum === true;
});

/**
 * When the subscription cancels, as UTC-start-of-day unix seconds. Only defined while the account
 * is pending cancellation and on an Ordway subscription; reads the cancellation date from the Ordway
 * customer's custom fields, falling back to the subscription's.
 */
const cancelsAt = computed([accountStore, subscription, customer], (account, sub, cust): number | undefined => {
  if (account?.account_state !== AccountState.PendingCancellation) {
    return undefined;
  }

  if (!isOrdwaySubscription(sub)) {
    return undefined;
  }

  let date: string | null | undefined;

  if (cust != null) {
    date = ordwayCustomerCustomFields(cust).cancellation_date;
  }

  if (isEmptyString(date)) {
    date = sub.cancellation_date;
  }

  return isEmptyString(date) ? undefined : utcStartOfDayUnix(date);
});

/** Whether the subscription can be cancelled. */
const canCancel = computed([accountStore, subscription, billingInfo], (account, sub, info) => {
  if (isOrdwaySubscription(sub)) {
    if (account?.account_state === AccountState.PendingCancellation) {
      return false;
    }

    return sub.canCancel;
  }

  return info?.cancellable ?? false;
});

/**
 * Whether the account is eligible to downgrade to Builder. Only Ordway accounts on a free trial
 * (Ordway models every trial with a single Free Trial product) are eligible.
 */
const isEligibleForBuilderDowngrade = computed([subscription, isTrial], (sub, trial) => {
  return isOrdwaySubscription(sub) ? trial : false;
});

// --- Pricing, rate & summary derivations. ---

/** The base price of the plan, in cents. */
const planPrice = computed([subscription], (sub): number | undefined => {
  if (isOrdwaySubscription(sub)) {
    return sub.basePrice;
  }

  return isStripeSubscription(sub) ? sub.plan?.basePrice : undefined;
});

/** The per-customer overage rate as a dollar-formatted string. */
const meteredCustomerRate = computed([subscription, isTrial], (sub, trial): string | undefined => {
  if (isOrdwaySubscription(sub)) {
    if (trial) {
      return Plans.Essentials.profileOverageRate;
    }

    if (sub.customerOverageRate === '') {
      return sub.profileOverageRate;
    }

    return sub.customerOverageRate;
  }

  return isStripeSubscription(sub) ? sub.plan?.customerOverageRate : undefined;
});

/** The per-object overage rate as a dollar-formatted string. */
const meteredObjectRate = computed([subscription], (sub): string | undefined => {
  if (isOrdwaySubscription(sub)) {
    return sub.objectOverageRate;
  }

  return isStripeSubscription(sub) ? sub.plan?.objectOverageRate : undefined;
});

/** The display name of the billing interval (e.g. "Monthly", "Yearly"). */
const intervalDisplayName = computed([subscription], (sub): string => {
  if (isOrdwaySubscription(sub)) {
    return sub.intervalDisplayName;
  }

  return (isStripeSubscription(sub) ? sub.plan?.intervalDisplayName : undefined) ?? 'unknown';
});

/**
 * The CDP Premium price, in cents. Ordway looks up the CDP Premium product on the subscription and
 * converts its dollar `list_price` string to cents; Stripe reads the plan's `cdpPremiumPrice`.
 * Undefined when the Ordway subscription has no CDP Premium product.
 */
const cdpPremiumPrice = computed([subscription], (sub): number | undefined => {
  if (isOrdwaySubscription(sub)) {
    const cdpPremiumProduct = (sub.plans ?? []).find(
      (product) => product.product_id === ordwayProductIds()[ProductName.CDPPremium],
    );

    if (cdpPremiumProduct == null) {
      return undefined;
    }

    return Number.parseFloat(cdpPremiumProduct.list_price) * 100;
  }

  return isStripeSubscription(sub) ? sub.plan?.cdpPremiumPrice : undefined;
});

/**
 * When the coupon ends, as unix seconds. Ordway reads the coupon's `expiration_date` as local
 * end-of-day unix seconds; Stripe reads the subscription's `coupon_ends_at`. Only the Ordway coupon
 * model carries `expiration_date`, so narrow the coupon union before reading it.
 */
const couponEndsAt = computed([subscription, coupon], (sub, couponValue): number | undefined => {
  if (isOrdwaySubscription(sub)) {
    const expiration =
      couponValue != null && 'expiration_date' in couponValue ? couponValue.expiration_date : undefined;
    // The Ordway wire model represents an unset date as an empty string, not null (matching the
    // other Ordway date getters). Guard with `isEmptyString` so `''` doesn't reach `parseISO` and
    // yield NaN.
    return isEmptyString(expiration) ? undefined : localEndOfDayUnix(expiration);
  }

  return isStripeSubscription(sub) ? sub.coupon_ends_at : undefined;
});

/**
 * The account's startup program tier, if any. Ordway derives the tier from which startup product
 * the subscription includes; Stripe reads the billing info's `startup_program`.
 */
const startupProgram = computed([subscription, billingInfo], (sub, info): string | undefined => {
  if (isOrdwaySubscription(sub)) {
    if (subscriptionIncludesProduct(sub, ProductName.StartupEssentials)) {
      return StartupType.Essentials;
    }
    if (subscriptionIncludesProduct(sub, ProductName.StartupBasic)) {
      return StartupType.Basic;
    }
    if (subscriptionIncludesProduct(sub, ProductName.StartupPlus)) {
      return StartupType.Plus;
    }
    return undefined;
  }

  return info?.startup_program;
});

/**
 * The newest per-environment `updated_at` across the account usage. Reads the `usage` atom's
 * `environment_usages`. Returns undefined until usage is loaded (via `loadUsage`) or when there are
 * no environments.
 */
const usageSyncedAt = computed([usage], (accountUsage): number | undefined => {
  const environmentUsages = accountUsage?.environment_usages ?? [];
  if (environmentUsages.length === 0) {
    return undefined;
  }

  // Each workspace's usage is synced separately (usually within seconds of each other); the Ember
  // getter sorted ascending by updated_at and took the last, i.e. the newest. Reduce to the max
  // rather than sorting; never throw on sparse test data (missing updated_at compares as NaN, which
  // simply never wins the max).
  return environmentUsages.reduce<number | undefined>((newest, environment) => {
    const updatedAt = environment.updated_at;
    if (newest == null || updatedAt > newest) {
      return updatedAt;
    }
    return newest;
  }, undefined);
});

/**
 * The billing overage estimates for the account. Ordway reads the estimate stored via
 * {@link billing.setOrdwayBillingEstimate}; legacy Stripe reads the billing info's `billing_estimates`.
 * The Stripe wire model has no `mau_overage`, so it's normalized to `0` to match the shared shape.
 */
const billingEstimates = computed(
  [subscription, billingInfo, ordwayBillingEstimate],
  (sub, info, ordwayEstimate): BillingEstimates | undefined => {
    if (isOrdwaySubscription(sub)) {
      return ordwayEstimate;
    }

    // Stripe: the OpenAPI `billing_estimates` (no mau_overage — that's Ordway-only, which the type
    // models as optional), returned as-is.
    return info?.billing_estimates;
  },
);

// Payment-method & charge-status derivations (ported from the Ember billing getters). Ordway reads
// the Ordway `customer`; Stripe reads the subscription's wire flags / `stripe_customer`.

/** Whether the account has a card on file. */
const hasCard = computed([subscription, customer], (sub, cust) => {
  if (isOrdwaySubscription(sub)) {
    return cust?.has_credit_card === true;
  }
  return isStripeSubscription(sub) && sub.hasCard;
});

/** Whether the account has ACH/direct-debit set up. */
const hasAchSetup = computed([subscription, customer], (sub, cust) => {
  if (isOrdwaySubscription(sub)) {
    return cust?.has_ach === true;
  }
  return isStripeSubscription(sub) && sub.has_ach_setup === true;
});

/** Whether the (Stripe) subscription has a failed overage charge. Always false for Ordway. */
const hasFailedOverageCharge = computed([subscription], (sub) => {
  if (!isStripeSubscription(sub)) {
    return false;
  }
  return sub.has_failed_overage_charge === true;
});

/** The raw Stripe customer, or undefined for Ordway. */
const stripeCustomer = computed([subscription], (sub) => {
  if (!isStripeSubscription(sub)) {
    return undefined;
  }
  return sub.stripe_customer;
});

/** Whether the account can retry a failed payment. */
const canRetryPayment = computed(
  [subscription, customer, billingInfo, accountStore, hasFailedOverageCharge],
  (sub, cust, info, account, failedOverage) => {
    if (isOrdwaySubscription(sub)) {
      const state = account?.account_state;
      const suspendedOrOverdue = state === AccountState.Suspended || state === AccountState.Overdue;
      return (suspendedOrOverdue && cust?.has_ach === false) || failedOverage;
    }
    return info?.can_retry_payment === true;
  },
);

/** The most recent payment error, formatted for display, or null. */
const lastPaymentError = computed([subscription, customer], (sub, cust) => {
  if (isOrdwaySubscription(sub)) {
    return cust?.payment_failure_reason ?? null;
  }
  if (!isStripeSubscription(sub)) {
    return null;
  }
  const error = sub.stripe_customer?.last_payment_error;
  if (error == null) {
    return null;
  }
  return stripeFormatter(error);
});

export const billing = {
  isOrdwayCustomer,
  isOrdwayCustomerWaitingInitialization,
  isVerificationRejected,

  initialized,
  initializedAt: null as number | null,

  subscription,
  coupon,
  taxRate,
  paramPlan,
  billingInfo,
  plans,
  tiers,
  customer,
  futureSubscription,
  products,
  usage,
  availablePlans,

  isPremiumTrial,
  isPendingCheckout,
  isTrial,
  pinnedPlan,
  essentialsPlan,
  billingPeriodStart,
  billingPeriodEnd,
  includedProfiles,
  includedMessages,
  includedObjects,
  includedMau,
  totalCustomerCount,
  isUpgradeDisabled,
  upgradeDisabledMessage,
  disabledMessageForPermission,

  planType,
  futurePlanType,
  planSlug,
  isBasic,
  isPremium,
  isBasicTrial,
  planNameShortened,
  planDisplayName,
  isBuilder,
  isBuilderPlan,
  isFreeBuilder,
  upgradePlanTypes,

  // Subscription lifecycle & date derivations (computeds). Mirrored by the Ember billing service.
  isSubscriptionActive,
  pinnedStartDate,
  formattedStartDate,
  isAnnualPlan,
  isLumpSumPlan,
  cancelsAt,
  canCancel,
  isEligibleForBuilderDowngrade,

  // Pricing, rate & summary derivations (computeds). Mirrored by the Ember billing service.
  planPrice,
  meteredCustomerRate,
  meteredObjectRate,
  intervalDisplayName,
  cdpPremiumPrice,
  couponEndsAt,
  startupProgram,
  usageSyncedAt,
  billingEstimates,
  isEligibleForRetentionDiscount: retentionDiscountEligible,

  // Payment-method & charge-status derivations (mirrored by the Ember billing service).
  hasCard,
  hasAchSetup,
  hasFailedOverageCharge,
  stripeCustomer,
  canRetryPayment,
  lastPaymentError,
  /** The account's saved card in the vendor-neutral `PaymentCard` shape (the `card` atom). */
  cardDetails: card,

  /** Sets the tax rate atom. The canonical gateway for the tax-rate writers in the embedded Ember app. */
  setTaxRate(value?: TaxRate) {
    taxRate.set(value);
  },

  /** Clears the tax rate atom. Ports the Ember `billing.resetTaxRate`. */
  resetTaxRate() {
    taxRate.set(undefined);
  },

  /** Sets the Ordway billing estimate atom. Called by the embedded Ember billing shell's `fetchOrdwayData`. */
  setOrdwayBillingEstimate(value?: BillingEstimates) {
    ordwayBillingEstimate.set(value);
  },

  /** Sets the retention-discount eligibility atom. Called by the embedded Ember `checkRetentionDiscountEligibility`. */
  setRetentionDiscountEligible(value: boolean) {
    retentionDiscountEligible.set(value);
  },

  /**
   * Resets every account-scoped atom back to its initial value, giving the service a clean slate for
   * a new account. Called on an in-app account switch (e.g. masquerade) before re-initializing —
   * unlike a page reload, an in-app switch keeps these module-level atoms alive, so without this the
   * previous account's data (including the lazily-loaded `availablePlans` / `ordwayBillingEstimate` /
   * `retentionDiscountEligible` / `usage`, which no refresh path touches) can leak across the switch.
   * Resets everything rather than cherry-picking for consistency and robustness. Clearing `initialized`
   * lets the subsequent `initialize.perform` re-run fully.
   */
  handleAccountSwitch() {
    subscription.set(undefined);
    coupon.set(undefined);
    taxRate.set(undefined);
    paramPlan.set(null);
    billingInfo.set(undefined);
    plans.set(undefined);
    tiers.set(undefined);
    customer.set(undefined);
    futureSubscription.set(undefined);
    products.set(undefined);
    usage.set(undefined);
    availablePlans.set(undefined);
    ordwayBillingEstimate.set(undefined);
    card.set(null);
    retentionDiscountEligible.set(false);
    initialized.set(false);
    billing.initializedAt = null;
  },

  /**
   * Whether the subscription's start date is in the future. A plain getter (not a computed) because
   * the Stripe branch compares the pinned start date against the current time.
   */
  get startDateInFuture(): boolean {
    const sub = subscription.get();
    if (isOrdwaySubscription(sub)) {
      return futureSubscription.get() != null;
    }

    // Non-Ordway branch, so the pinned start date is `billingInfo.pinned_start_date` (see
    // `pinnedStartDate`); read it directly.
    return (billingInfo.get()?.pinned_start_date ?? 0) > getUnixTime(new Date());
  },

  // Date-based trial derivations. Kept as getters (rather than computeds) because they depend on
  // the current time, so each read recomputes against "now" — matching the Ember getters.
  /** When the trial expires, as unix seconds, or null. */
  get trialExpiresAt(): number | null {
    const sub = subscription.get();
    if (isOrdwaySubscription(sub)) {
      return utcStartOfDayUnix(sub.billing_start_date);
    }

    const account = accountStore.get();
    if (isTrial.get()) {
      return account?.suspend_trial_at ?? null;
    }

    return account?.planFeatures.messaging.expiresAt ?? null;
  },

  /** The number of days left in the trial. */
  get daysLeftInTrial(): number {
    const expiresAt = this.trialExpiresAt;
    if (expiresAt == null) {
      return 0;
    }

    return differenceInDays(fromUnixTime(expiresAt), new Date());
  },

  /** Whether the trial has expired. */
  get trialExpired(): boolean {
    if (!isTrial.get()) {
      return false;
    }

    const account = accountStore.get();
    const sub = subscription.get();
    if (isOrdwaySubscription(sub)) {
      const startOfDayMs = utcStartOfDayUnix(sub.billing_start_date) * 1000;
      return account?.account_state !== AccountState.Current && startOfDayMs < Date.now();
    }

    const suspendTrialAt = account?.suspend_trial_at;
    const suspendedInPast = suspendTrialAt != null && suspendTrialAt * 1000 < Date.now();
    return suspendedInPast || account?.planFeatures.messaging.expired === true;
  },

  /**
   * Whether the trial has been suspended. A plain getter (not a computed) because the Ordway branch
   * reads the time-relative {@link billing.trialExpired}.
   */
  get isTrialSuspended(): boolean {
    if (isOrdwayCustomer.get()) {
      // Check against the account, since there could be no effective subscription for a
      // suspended account.
      const accountState = accountStore.get()?.account_state;
      const isCanceledOrSuspended = accountState === AccountState.Canceled || accountState === AccountState.Suspended;
      return isCanceledOrSuspended && (subscription.get() == null || this.trialExpired);
    }

    return billingInfo.get()?.trial_suspended ?? false;
  },

  /**
   * Whether the trial is suspending soon. A plain getter because it reads the time-relative
   * {@link billing.daysLeftInTrial}.
   */
  get trialSuspendingSoon(): boolean {
    return this.daysLeftInTrial <= 3 && accountStore.get()?.account_state === AccountState.Free;
  },

  /**
   * The plan name, with a trial/past-due status suffix when applicable. A plain getter (not a
   * computed) because it reads the time-relative {@link billing.isTrialSuspended} /
   * {@link billing.trialSuspendingSoon}.
   */
  get planName(): string {
    const nameShort = planNameShortened.get();

    if (isTrial.get()) {
      if (this.trialSuspendingSoon) {
        return `${nameShort} Expiring Soon`;
      }
      if (this.isTrialSuspended) {
        return `${nameShort} Expired`;
      }
    } else if (accountStore.get()?.account_state === AccountState.Overdue) {
      return `${nameShort} Past Due`;
    }

    return nameShort;
  },

  /**
   * The formatted, relative-to-now time left in the trial (e.g. "in 3 days" / "3 days ago"). A
   * plain getter (not a computed) because it renders a relative-to-now distance, so each read
   * recomputes against "now". Uses date-fns `formatDistanceToNow(..., { addSuffix: true })`.
   */
  get formattedTimeLeftInTrial(): string {
    const sub = subscription.get();
    if (isOrdwaySubscription(sub)) {
      return formatDistanceToNow(fromUnixTime(utcStartOfDayUnix(sub.billing_start_date)), { addSuffix: true });
    }

    // Guard a missing suspend_trial_at: `fromUnixTime(0)` would anchor the relative time to the
    // 1970 epoch (and `formatDistanceToNow` throws on an invalid date). A stripe trial normally
    // carries this, so this only trips on sparse data; render nothing rather than a bogus date.
    const suspendTrialAt = accountStore.get()?.suspend_trial_at;
    if (suspendTrialAt == null) {
      return '';
    }
    return formatDistanceToNow(fromUnixTime(suspendTrialAt), { addSuffix: true });
  },

  /**
   * A human-readable summary of the account's billing status. A plain getter (not a computed)
   * because it reads the time-relative {@link billing.formattedTimeLeftInTrial} /
   * {@link billing.daysLeftInTrial}.
   */
  get planSummary(): string {
    const account = accountStore.get();

    if (account?.account_state === AccountState.Overdue) {
      return 'Your account is overdue';
    }
    if (this.isTrialSuspended) {
      // daysLeftInTrial is negative once the trial has ended; pluralize on the same absolute value
      // shown to the user so "ended 1 day ago" (not "1 days ago"). (Fixes a latent Ember bug that
      // pluralized on the signed value.)
      const daysSinceTrialEnded = Math.abs(this.daysLeftInTrial);
      return `Your trial ended ${daysSinceTrialEnded} ${plur('day', daysSinceTrialEnded)} ago`;
    }
    if (isPendingCheckout.get()) {
      return `Your trial will automatically upgrade ${this.formattedTimeLeftInTrial}`;
    }
    if (isTrial.get()) {
      return `Your trial expires ${this.formattedTimeLeftInTrial}`;
    }
    return `Your account is ${account?.account_state}`;
  },

  setStripeBillingProperties(res: BillingGetInfoResponse, paramPlanSlug: string) {
    const context = stripePlanContext(res.billing_info);
    const stripePlans = (res.plans ?? []).map((plan) => stripePlanToBillingPlan(plan, context));

    this.billingInfo.set(res.billing_info ?? undefined);
    this.subscription.set(
      res.subscription == null ? undefined : stripeSubscriptionToBillingData(res.subscription, stripePlans),
    );
    this.coupon.set(res.coupon ?? undefined);
    this.plans.set(stripePlans);
    this.tiers.set(res.tiers ?? []);
    this.taxRate.set(res.tax_rate ?? undefined);
    this.paramPlan.set(
      isEmptyString(paramPlanSlug) ? null : (stripePlans.find((plan) => plan.slug === paramPlanSlug) ?? null),
    );
    card.set(stripeSubscriptionCard(res.subscription));
    // reset ordway properties to clear stale data
    this.customer.set(undefined);
    this.futureSubscription.set(undefined);
    this.products.set(undefined);
  },

  // fallow-ignore-next-line complexity — ported from Ember billing, complexity is inherent to the domain mapping
  setOrdwayBillingProperties(res: BillingOrdwayProfileResponse) {
    const allSubscriptions = sortOrdwaySubscriptionsByDate(res.ordway_subscriptions ?? []);

    const effectiveSubscription = allSubscriptions[0];
    let upcomingSubscription = allSubscriptions.length > 1 ? allSubscriptions[1] : undefined;

    if (upcomingSubscription != null) {
      upcomingSubscription = {
        ...upcomingSubscription,
        plans: dedupeFutureSubscriptionProducts(upcomingSubscription.plans ?? []),
      };
    }

    this.customer.set(res.ordway_customer ?? undefined);
    this.subscription.set(
      effectiveSubscription == null ? undefined : ordwaySubscriptionToBillingPlan(effectiveSubscription),
    );
    this.futureSubscription.set(
      upcomingSubscription == null ? undefined : ordwaySubscriptionToBillingPlan(upcomingSubscription),
    );
    this.coupon.set(res.coupon ?? undefined);
    this.products.set(effectiveSubscription?.plans ?? []);
    // reset legacy stripe properties to clear stale data
    this.billingInfo.set(undefined);
    this.plans.set(undefined);
    this.tiers.set(undefined);
    // The Ordway card is loaded lazily by `fetchOrdwayCardDetails`, so clear any prior card here
    // (e.g. the previous account's on a same-runtime masquerade switch) to keep `cardDetails` honest.
    card.set(null);
  },

  /** Fetches the account's tax rate and stores it. Ports the Ember `billing.updateTaxRate` task. */
  updateTaxRate: enqueueTask(
    async ({
      country,
      state,
      zipCode,
      addressLine1,
    }: {
      country: string;
      state: string;
      zipCode: string;
      addressLine1?: string;
    }): Promise<void> => {
      const account = accountStore.get();
      if (account == null) {
        return;
      }

      try {
        // Ordway (Avalara) keys the lookup by region + street; Stripe (TaxJar) by state. Branch on
        // the account (ordway_id) like the other Ordway paths, not the subscription — an Ordway
        // account can hit this before its subscription has loaded (e.g. early checkout).
        const query = isOrdwayCustomer.get()
          ? { country, zip_code: zipCode, region: state, address1: addressLine1 }
          : { country, zip_code: zipCode, state };

        const res = await hydraUiApi.billing.getTaxRate({
          params: { path: { account_id: String(account.id) }, query },
        });
        billing.setTaxRate(res.tax_rate);
      } catch (error) {
        // A failed tax lookup shouldn't break the flow — report it and clear any stale rate.
        sentryCaptureException(error);
        billing.resetTaxRate();
      }
    },
  ),

  /** Fetches a Stripe setup intent for adding/updating a card. Ports `billing.getSetupIntent`. */
  getSetupIntent: enqueueTask(async () => {
    const account = accountStore.get();
    if (account == null) {
      throw new Error('Cannot create a setup intent without an account');
    }

    return hydraUiApi.billing.getSetupIntent({ params: { path: { account_id: String(account.id) } } });
  }),

  /** Fetches the account's Ordway card/payment details. Ports `billing.fetchOrdwayCardDetails`. */
  fetchOrdwayCardDetails: enqueueTask(async () => {
    const account = accountStore.get();
    if (account == null) {
      return undefined;
    }

    // Store the card in the vendor-neutral `card` atom (read via `cardDetails`) and still return the
    // raw payment method for the embedded Ember card-details component, which reads it directly.
    const payment = await hydraUiApi.billing.getPayment({ params: { path: { account_id: String(account.id) } } });
    card.set(ordwayPaymentToPaymentCard(payment));
    return payment;
  }),

  /**
   * Loads the available Ordway plans catalog into `availablePlans`. No-op for Stripe accounts,
   * whose plans arrive with `billing/info`. Ports `billing.fetchAvailablePlans`.
   */
  fetchAvailablePlans: enqueueTask(async (): Promise<void> => {
    if (!isOrdwayCustomer.get()) {
      return;
    }

    const account = accountStore.get();
    if (account == null) {
      return;
    }

    const res = await hydraUiApi.billing.getPlans({
      params: { path: { account_id: String(account.id) }, query: { product_type: 'journeys' } },
    });
    availablePlans.set((res.ordwayPlans ?? []).map(ordwayPlanToBillingPlan));
  }),

  /**
   * Checks retention-discount eligibility and stores it. Only Essentials, non-trial accounts can be
   * eligible; anything else resets to false. Ports `billing.checkRetentionDiscountEligibility`.
   */
  checkRetentionDiscountEligibility: enqueueTask(async (): Promise<void> => {
    billing.setRetentionDiscountEligible(false);

    if (planType.get() !== PlanType.Essentials || isTrial.get()) {
      return;
    }

    const account = accountStore.get();
    if (account == null) {
      return;
    }

    try {
      const res = await hydraUiApi.billing.getRetentionDiscount({
        params: { path: { account_id: String(account.id) } },
      });
      billing.setRetentionDiscountEligible(res.eligible === true);
    } catch {
      billing.setRetentionDiscountEligible(false);
    }
  }),

  /**
   * Polls `billing/subscription_{complete,exists}` until the subscription change lands. Resolves on
   * success; throws the server error, or an empty error on timeout. Cancelable via `cancelAll`.
   * Ports the Ember `pollForSubscriptionTask`.
   */
  pollForSubscriptionTask: pollTask(async (signal, type: 'complete' | 'exists'): Promise<void> => {
    const account = accountStore.get();
    if (account == null) {
      return;
    }
    const accountId = String(account.id);

    for (let attempt = 0; attempt < POLL_MAX_ATTEMPTS; attempt++) {
      if (signal.aborted) {
        return;
      }

      let setupError = '';
      try {
        const res =
          type === 'complete'
            ? await hydraUiApi.billing.getSubscriptionComplete({ params: { path: { account_id: accountId } } })
            : await hydraUiApi.billing.getSubscriptionExists({ params: { path: { account_id: accountId } } });
        if (isEmptyString(res.error)) {
          return;
        }
        setupError = res.error;
      } catch {
        // Request failed; retry.
      }

      if (!isEmptyString(setupError)) {
        throw new Error(setupError);
      }

      await pollDelay(POLL_INTERVAL_MS, signal);
    }

    // No success response after enough tries; an empty error is enough to signal the timeout.
    throw new Error();
  }),

  /**
   * Polls `billing/{ai_credits,builder_credits}/purchase_status` until the credits purchase is
   * granted. Resolves when granted; throws on failure/timeout. Cancelable via `cancelAll`. Ports
   * the Ember `pollForCreditsPurchaseTask`.
   */
  pollForCreditsPurchaseTask: pollTask(
    // fallow-ignore-next-line complexity — ported from Ember billing, complexity is inherent to the poll's status handling
    async (signal, creditType: 'ai_credits' | 'builder_credits', invoiceId: string): Promise<void> => {
      const account = accountStore.get();
      if (account == null) {
        return;
      }
      const accountId = String(account.id);
      const label = creditType === 'ai_credits' ? 'AI credits' : 'Added funds';
      let lastStatus: string | undefined;

      for (let attempt = 0; attempt < POLL_MAX_ATTEMPTS; attempt++) {
        if (signal.aborted) {
          return;
        }

        let status: string | undefined;
        try {
          const res =
            creditType === 'ai_credits'
              ? await hydraUiApi.billing.getAiCreditsPurchaseStatus({
                  params: { path: { account_id: accountId }, query: { invoice_id: invoiceId } },
                })
              : await hydraUiApi.billing.getBuilderCreditsPurchaseStatus({
                  params: { path: { account_id: accountId }, query: { invoice_id: invoiceId } },
                });
          status = res.status;
        } catch {
          // Request failed; retry.
        }

        if (status === 'granted') {
          return;
        }
        if (status === 'failed') {
          throw new Error('Your payment failed. Contact support.');
        }
        if (status != null) {
          lastStatus = status;
        }

        await pollDelay(POLL_INTERVAL_MS, signal);
      }

      if (lastStatus === 'processing') {
        throw new Error('Your payment was received and is still being processed. Your credits will appear shortly.');
      }

      sentryCaptureException(
        new Error(
          `${label} purchase polling timed out after ${POLL_MAX_ATTEMPTS} attempts for invoice ${invoiceId} (lastStatus: ${lastStatus ?? 'none'})`,
        ),
      );
      throw new Error("Your purchase couldn't be processed. Contact support.");
    },
  ),

  // Subscription/account mutation tasks. Each performs only the billing-REST mutation (and, where the
  // Ember task validated the response, that validation) and resolves on success or throws the
  // `HydraUIAPIError` on failure. The Ember-specific orchestration those tasks also do — snackbars,
  // router transitions, `refreshAccountState`/`initialize`, Ember-Data account mutations, store
  // unloads, `window.location` navigation — stays in the delegating Ember task shells, which call
  // these and then run their follow-up. Ports the mutation half of the Ember billing tasks.

  /**
   * Pauses the (Stripe) subscription. The Stripe-only precondition and the success/error snackbars
   * stay in the Ember `pauseSubscription` shell. Ports the mutation half of that task.
   */
  pauseSubscription: enqueueTask(async (feedback: string): Promise<void> => {
    const account = accountStore.get();
    if (account == null) {
      return;
    }

    const body: BillingPauseRequest = { action: 'pause', feedback };
    await hydraUiApi.billing.subscriptionsPause({ params: { path: { account_id: String(account.id) } }, body });
  }),

  /**
   * Unpauses (reactivates) the subscription. The success/error snackbars stay in the Ember
   * `unpauseSubscription` shell. Ports the mutation half of that task. The pause endpoint's request
   * body requires `feedback`; unpause carries none, so send an empty string (the server ignores it).
   */
  unpauseSubscription: enqueueTask(async (): Promise<void> => {
    const account = accountStore.get();
    if (account == null) {
      return;
    }

    const body: BillingPauseRequest = { action: 'unpause', feedback: '' };
    await hydraUiApi.billing.subscriptionsPause({ params: { path: { account_id: String(account.id) } }, body });
  }),

  /**
   * Applies the 50%-off retention discount. Reloading billing data, the success snackbar, and the
   * already-redeemed (HTTP 409) vs generic error messaging stay in the Ember `applyRetentionDiscount`
   * shell (which inspects the thrown `HydraUIAPIError.status`). Ports the mutation half of that task.
   */
  applyRetentionDiscount: enqueueTask(async (): Promise<void> => {
    const account = accountStore.get();
    if (account == null) {
      return;
    }

    await hydraUiApi.billing.createRetentionDiscount({ params: { path: { account_id: String(account.id) } } });
  }),

  /**
   * Downgrades the account to the free Builder plan. The success snackbar and the subsequent hard
   * page reload stay in the Ember `downgradeToBuilder` shell. Ports the mutation half of that task.
   * The endpoint expects an empty JSON object (its services-side route rejects an empty body).
   */
  downgradeToBuilder: enqueueTask(async (): Promise<void> => {
    const account = accountStore.get();
    if (account == null) {
      return;
    }

    await hydraUiApi.billing.downgradeToBuilder({ params: { path: { account_id: String(account.id) } }, body: {} });
  }),

  /**
   * Cancels the Ordway subscription (the `billing/subscriptions/cancel` mutation shared by the Ember
   * `cancelAccountDeferred` task and the Ordway branch of `cancelAccountImmediate`). The snackbars,
   * feedback delay, store unload, and navigation stay in those Ember shells. The Stripe cancel path
   * (`account.current.cancel`, an Ember-Data mutation) stays in Ember. Ports the mutation half.
   */
  // Two vendor cancel endpoints share the same trivial request shape; a wrapper obscures more than it saves.
  // fallow-ignore-next-line code-duplication
  cancelOrdwaySubscription: enqueueTask(async (feedback: string, switchingTo: string): Promise<void> => {
    const account = accountStore.get();
    if (account == null) {
      return;
    }

    const body: BillingCancelOrdwaySubscriptionRequest = { feedback, switching_to: switchingTo };
    await hydraUiApi.billing.subscriptionsCancel({ params: { path: { account_id: String(account.id) } }, body });
  }),

  /**
   * Cancels the account for a legacy Stripe customer (the `accounts/{id}/cancel` mutation the Stripe
   * branch of the Ember `cancelAccountImmediate` task ran through the Ember-Data account model — a
   * plain POST with no Ember-Data dependency). The confirm prompt, feedback delay, account-store
   * unload, snackbars, and navigation stay in that Ember shell. The Ordway cancel path is
   * `cancelOrdwaySubscription` above. Ports the mutation half of the Stripe cancel.
   */
  cancelStripeSubscription: enqueueTask(async (feedback: string, switchingTo: string): Promise<void> => {
    const account = accountStore.get();
    if (account == null) {
      return;
    }

    const body: BillingCancelRequest = { feedback, switching_to: switchingTo };
    await hydraUiApi.billing.cancel({ params: { path: { account_id: String(account.id) } }, body });
  }),

  /**
   * Activates a draft Ordway subscription and confirms the server reports it Active — throwing if not,
   * matching the Ember task, so the shell treats a non-Active result as a failure. Refreshing account
   * state, the success snackbar, the router transition, and Sentry reporting stay in the Ember
   * `activateSubscriptionInOrdway` shell. Ports the mutation half of that task.
   */
  activateSubscriptionInOrdway: enqueueTask(async (subscriptionId: string): Promise<void> => {
    const account = accountStore.get();
    if (account == null) {
      return;
    }

    const res = await hydraUiApi.billing.subscriptionsActivate({
      params: { path: { account_id: String(account.id), subscription_id: subscriptionId } },
    });

    const activated = res.ordway_subscriptions?.[0]?.status === 'Active';
    if (!activated) {
      throw new Error('Subscription activation failed, no active subscription found');
    }
  }),

  /**
   * Subscribes the account to an Ordway plan. The Ember shell reads the account's billing-contact
   * fields off the Ember-Data account model and passes them here as the request body; refreshing
   * account state, the success snackbar, and the router transition stay in the Ember
   * `startSubscriptionInOrdway` shell. Ports the mutation half of the Ember `subscribeToPlanInOrdway`.
   */
  subscribeToPlanInOrdway: enqueueTask(
    async (planId: string, contact: BillingOrdwaySubscriptionRequest): Promise<void> => {
      const account = accountStore.get();
      if (account == null) {
        return;
      }

      await hydraUiApi.billing.createPlans({
        params: { path: { account_id: String(account.id), plan_id: planId } },
        body: contact,
      });
    },
  ),

  /**
   * Updates the account's card from a confirmed Stripe SetupIntent (a `billing/card` POST). The Ember
   * shell keeps the in-progress/success/error snackbars and, for Stripe accounts, the subscription
   * poll + refresh. Ports the mutation half of the Ember `billing.updateCard`.
   */
  updateCard: enqueueTask(async (setupIntent?: GithubComStripeStripeGoV71SetupIntent): Promise<void> => {
    const account = accountStore.get();
    if (account == null) {
      return;
    }

    await hydraUiApi.billing.card({
      params: { path: { account_id: String(account.id) } },
      body: { setup_intent: setupIntent },
    });
  }),

  /**
   * Subscribes the account to a Stripe plan from a confirmed SetupIntent (a `subscribe` POST). The
   * Ember shell owns the plan/coupon selection, the subscription poll, account-state refresh,
   * success/error snackbars, and the router transition. `annual` is intentionally not sent — the
   * server derives annual-ness from the plan. Ports the mutation half of the Ember
   * `billing.startSubscription`.
   */
  startSubscription: enqueueTask(
    async (
      planId: string | undefined,
      couponCode: string,
      setupIntent?: GithubComStripeStripeGoV71SetupIntent,
    ): Promise<void> => {
      const account = accountStore.get();
      if (account == null) {
        return;
      }

      await hydraUiApi.billing.subscribe({
        params: { path: { account_id: String(account.id) } },
        body: { plan_id: Number(planId), coupon_code: couponCode, setup_intent: setupIntent },
      });
    },
  ),

  /**
   * Creates a Stripe PaymentIntent for a credits purchase, returning its client secret and invoice
   * id. Ports the Ember `billing.createCreditsPaymentIntent`. AI-credits and builder-credits have
   * distinct request bodies — only builder-credits accepts `save_card` — so dispatch to the matching
   * typed endpoint. (The Ember method sent `save_card` for both; the AI-credits endpoint never read
   * it, so omitting it here is behavior-preserving.)
   */
  async createCreditsPaymentIntent(
    creditType: 'ai_credits' | 'builder_credits',
    quantity: number,
    address: { country: string; state: string; city: string; zipCode: string; street: string },
    useSavedCard = false,
    saveCard = false,
  ): Promise<{ client_secret: string; invoice_id: string }> {
    const account = accountStore.get();
    if (account == null) {
      throw new Error('Cannot create a credits payment intent without an account');
    }

    const params = { path: { account_id: String(account.id) } };

    if (creditType === 'ai_credits') {
      return hydraUiApi.billing.aiCreditsPaymentIntent({
        params,
        body: {
          quantity,
          country: address.country,
          state: address.state,
          city: address.city,
          zip_code: address.zipCode,
          street: address.street,
          use_saved_card: useSavedCard,
        },
      });
    }

    return hydraUiApi.billing.builderCreditsPaymentIntent({
      params,
      body: {
        quantity,
        country: address.country,
        state: address.state,
        city: address.city,
        zip_code: address.zipCode,
        street: address.street,
        use_saved_card: useSavedCard,
        save_card: saveCard,
      },
    });
  },

  /**
   * Purchases AI credits for an Ordway account, returning the purchase status. Ports the Ember
   * `billing.createAICreditsOrdwayPurchase`.
   */
  async createAICreditsOrdwayPurchase(quantity: number): Promise<{ status: string }> {
    const account = accountStore.get();
    if (account == null) {
      throw new Error('Cannot purchase AI credits without an account');
    }

    return hydraUiApi.billing.aiCreditsOrdwayPurchase({
      params: { path: { account_id: String(account.id) } },
      body: { quantity },
    });
  },

  /** Sets the account's default payment method. Ports the Ember `billing.setDefaultPaymentMethod`. */
  async setDefaultPaymentMethod(paymentMethodId: string): Promise<void> {
    const account = accountStore.get();
    if (account == null) {
      throw new Error('Cannot set a default payment method without an account');
    }

    await hydraUiApi.billing.paymentMethodDefault({
      params: { path: { account_id: String(account.id) } },
      body: { payment_method_id: paymentMethodId },
    });
  },

  /**
   * Lazily loads account usage into the `usage` atom. Not part of `initialize` — call it on demand
   * (e.g. via the `useBillingUsage` hook), mirroring the Ember routes that fetch account-usage per
   * page rather than on boot. Enqueued so concurrent callers share one in-flight load.
   */
  loadUsage: enqueueTask(async ({ refresh = false }: { refresh?: boolean } = {}): Promise<void> => {
    const account = accountStore.get();
    if (account == null) {
      return;
    }

    // Surface load failures like the TanStack `meta.errorMessage` pipeline the workspaces page
    // used before it read usage through the service: report to Sentry and show the same toast,
    // rather than swallowing the rejection into a voided promise in the caller.
    try {
      usage.set(await loadAccountUsage(account.id, refresh));
    } catch (error) {
      sentryCaptureException(error, { extra: { accountId: account.id } });
      snackbarApi.create({
        variant: 'error',
        id: 'billing.loadUsage.error',
        message: 'Could not load workspace usage. Please try again.',
      });
    }
  }),

  /**
   * Loads billing data for the current account into the stores above.
   *
   * An enqueued task (à la ember-concurrency): call `billing.initialize.perform(...)`.
   * Concurrent callers are serialized so they share one in-flight load; `perform`
   * is a closure, so it stays callable when destructured or invoked off the window
   * global. The body references `billing` directly (not `this`) since it runs
   * outside the object's method context.
   */
  initialize: enqueueTask(
    // fallow-ignore-next-line complexity — ported from Ember billing, complexity is inherent to the domain mapping
    async ({
      refresh = false,
      ignoreErrors = false,
      paramPlanSlug = '',
      includeCoupons = false,
    }: InitializeBillingOptions = {}): Promise<void> => {
      if (billing.initialized.get() && !refresh) {
        return;
      }

      // If the service was recently initialized, return early even when refreshing,
      // since the user probably hasn't taken an action that warrants a refresh
      // (we're likely just redirecting). A plan param means the data would differ,
      // so don't short-circuit it.
      const initializedAt = billing.initializedAt ?? 0;
      const recentlyInitialized = Date.now() - initializedAt <= RECENTLY_INITIALIZED_WINDOW_MS;

      if (billing.initialized.get() && refresh && paramPlanSlug === '' && recentlyInitialized) {
        return;
      }

      const account = accountStore.get();
      if (account == null) {
        return;
      }

      const isOrdway = billing.isOrdwayCustomer.get();

      // The Ordway profile endpoint is expected to fail for suspended/canceled
      // accounts and for customers still waiting on Ordway provisioning, so widen
      // `ignoreErrors` for those cases (matching the Ember service).
      const ordwayIgnoreErrors =
        ignoreErrors ||
        account.account_state === AccountState.Suspended ||
        account.account_state === AccountState.Canceled ||
        billing.isOrdwayCustomerWaitingInitialization.get();

      const effectiveIgnoreErrors = isOrdway ? ordwayIgnoreErrors : ignoreErrors;

      try {
        if (isOrdway) {
          const res = await loadOrdwayProfile(account.id, includeCoupons, refresh);
          billing.setOrdwayBillingProperties(res);
        } else {
          const res = await loadStripeInfo(account.id, paramPlanSlug, refresh);
          billing.setStripeBillingProperties(res, paramPlanSlug);
        }

        billing.initialized.set(true);
        billing.initializedAt = Date.now();

        if (refresh) {
          // A refresh means billing state may have changed server-side (plan
          // change, new subscription). Account-derived UI (nav gating, home
          // screen) hangs off the cached auth, so refresh that too.
          void announceRefreshAuth();
        }
      } catch (error) {
        if (effectiveIgnoreErrors) {
          billing.initialized.set(true);
          billing.initializedAt = Date.now();
          return;
        }
        throw error;
      }
    },
  ),
};

/** Public API surface of the Hydra billing service. */
export type BillingService = typeof billing;
